<script>
  import { mapGetters } from "vuex";
  import { getAllowedActionsForModule } from "@/config/moduleActions";
  import historyCardVue from "@/components/historyCard/historyCard.vue";
  import NoFoundData from "@/components/organisms/NoFoundData.vue";

  export default {
    components: { ApplicationCardRole: () => import("./ApplicationCardRole.vue"), historyCardVue, NoFoundData },
    data() {
      return {
        userApps: [],
        reset: 1,
        permissionsByApp: {},
        permissionsDirty: false,
        permissionsSaving: {},
        pendingAfterSave: new Set(),
        permissionsSaveTimers: {},
        switchView: true,
        dictStatistics: {
          statistics: "producción",
          "rooms-history": "ocupación",
          models: "modelos",
        },
        historyItems: [],
      };
    },
    computed: {
      ...mapGetters("applications", { getAppsInformation: "getAppsInformation" }),
      getAppsSelectedWithRole() {
        return this.userApps.filter(({ isSelected, role, key }) => isSelected && role && key !== "all");
      },
      user() {
        return this.$store.state.user.dataUser;
      },
      isEditUser() {
        return !!(this.user?._id || this.editingUserId);
      },
      editingUserId() {
        return this.$store.state.user.userSelected?._id || this.user?._id;
      },
      historyCards() {
        return (this.historyItems || []).map((item) => {
          const isInactive = !!item?.deactivatedAt;

          const user = isInactive ? item?.deactivatedBy : item?.activatedBy;
          const date = isInactive ? item?.deactivatedAt : item?.activatedAt;
          const moduleKey = item?.module || item?.app || "";
          const moduleLabel = this.dictStatistics[moduleKey] || moduleKey;
          const actionLabel = isInactive ? "inactivo" : "activo";
          const displayName = this.getUserDisplayName(user);

          return {
            text: `${actionLabel} el módulo`,
            _id: item?._id || `${moduleKey}-${date}`,
            date: this.formatHistoryDate(date),
            module: `${moduleLabel}.`,
            name: displayName,
            user,
          };
        });
      },
    },
    watch: {
      getAppsSelectedWithRole(newVal) {
        const rolesApp = newVal.map(({ key, role, modules, permissionId }) => ({ [key]: this.buildRoleEntry(role, modules, permissionId) }));
        const convertToObject = Object.assign({}, ...rolesApp);
        this.$store.state.user.dataUser.rolesApp = convertToObject;
        const autologinEntry = convertToObject.autologin;
        const autologinRole = this.getRoleFromEntry(autologinEntry);
        this.$store.state.user.dataUser.roles = autologinRole ? [autologinRole] : [];
        this.verifyNext();
      },
      async editingUserId(newVal, oldVal) {
        if (newVal && newVal !== oldVal) {
          await this.loadPermissions();
          this.userApps = this.createAppsOptions();
        }
      },
    },
    methods: {
      getUserDisplayName(user) {
        if (!user) return "Usuario";

        const lastName = user?.lastname.split(" ")?.[0] || "";

        const name = [user?.name, lastName].filter(Boolean).join(" ").trim();

        return name || user?.user || "Usuario";
      },
      formatHistoryDate(date) {
        if (!date) return "";
        return new Date(date).toLocaleDateString("es-CO", { timeZone: "America/Bogota", day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true });
      },
      buildRoleEntry(role, modules = [], permissionId = null) {
        const entry = [role];
        entry.modules = Array.isArray(modules) ? modules : [];
        entry.permissionId = permissionId || null;
        return entry;
      },
      getRoleFromEntry(entry) {
        if (!entry) return "";
        if (Array.isArray(entry)) return entry[0] || "";
        if (typeof entry === "string") return entry;
        return entry?.role || "";
      },
      getPermissionIdFromEntry(entry) {
        if (!entry) return "";
        if (Array.isArray(entry)) return entry.permissionId || "";
        return entry?.permissionId || "";
      },
      getModulesFromEntry(entry) {
        if (!entry) return [];
        if (Array.isArray(entry)) return Array.isArray(entry.modules) ? entry.modules : [];
        return Array.isArray(entry.modules) ? entry.modules : [];
      },
      async loadPermissions() {
        if (!this.editingUserId) return;
        try {
          const { data } = await this.$axios.get("/permissions", { params: { userId: this.editingUserId } });
          const permissions = Array.isArray(data?.permissions) ? data.permissions : [];

          this.permissionsByApp = permissions.reduce((acc, perm) => {
            if (!perm?.app) return acc;
            acc[perm.app] = {
              _id: perm._id,
              role: perm.role,
              modules: Array.isArray(perm.modules) ? perm.modules : [],
            };
            return acc;
          }, {});
        } catch (error) {
          console.log("Error fetching permissions", error);
          this.permissionsByApp = {};
        }
      },
      normalizeActionsForPayload(moduleKey, actions = []) {
        const allowed = new Set([...getAllowedActionsForModule(moduleKey), "enable", "disable"]);
        return (Array.isArray(actions) ? actions : []).filter((action) => allowed.has(action));
      },
      async savePermissionsForUser(userId, assignedBy, appKeys = []) {
        if (!userId) return { success: false, error: "Usuario sin id" };
        const targetAppsRaw = appKeys.length > 0 ? this.getAppsSelectedWithRole.filter((app) => appKeys.includes(app.key)) : this.getAppsSelectedWithRole;
        const targetApps = targetAppsRaw.length ? targetAppsRaw : [];

        if (!targetApps.length) return { success: true };

        const toDelete = [];
        const toSave = [];

        targetApps.forEach((app) => {
          const modules = (app.modules || []).map(({ module, actions }) => ({
            module,
            actions: this.normalizeActionsForPayload(module, actions),
          }));
          app.modules = modules;

          const hasActions = modules.some(({ actions }) => Array.isArray(actions) && actions.length);

          if (!hasActions && app.permissionId) {
            toDelete.push({ appKey: app.key, permissionId: app.permissionId, app });
            return;
          }

          toSave.push({ app, payload: { userId, assignedBy, role: app.role, app: app.key, modules } });
        });

        const errors = [];

        for (const item of toDelete) {
          const { success, error } = await this.deletePermission(item.permissionId);
          if (!success) {
            errors.push(error || "Error al eliminar permisos");
            continue;
          }

          const updated = { ...this.permissionsByApp };
          delete updated[item.appKey];
          this.permissionsByApp = updated;
          item.app.permissionId = null;
        }

        for (const { app, payload } of toSave) {
          try {
            if (app.permissionId) {
              await this.$axios.put(`/permissions/${app.permissionId}`, payload);
            } else {
              const { data } = await this.$axios.post("/permissions", payload);
              const newId = data?.permission?._id || data?._id || null;
              if (newId) app.permissionId = newId;
            }

            this.permissionsByApp = {
              ...this.permissionsByApp,
              [app.key]: { _id: app.permissionId, role: app.role, modules: payload.modules },
            };
          } catch (error) {
            const err = error?.response?.data?.error || error?.message || "Error al guardar permisos";
            errors.push(err);
          }
        }

        if (errors.length) {
          return { success: false, error: errors[0] };
        }

        return { success: true, toSave };
      },
      async deletePermission(permissionId) {
        if (!permissionId) return { success: true };
        try {
          await this.$axios.delete(`/permissions/${permissionId}`);
          return { success: true };
        } catch (error) {
          const err = error?.response?.data?.error || error?.message || "Error al eliminar permisos";
          return { success: false, error: err };
        }
      },
      async handlePermissionsChange(payload) {
        this.permissionsDirty = true;

        if (this.isEditUser && payload?.remove) {
          const { permissionId, appKey } = payload;
          const targetPermissionId = permissionId || this.permissionsByApp?.[appKey]?._id;
          const { success, error } = await this.deletePermission(targetPermissionId);
          if (!success) {
            this.$toast?.error?.(error);
          } else {
            this.permissionsDirty = false;
            if (appKey) {
              const app = this.userApps.find((item) => item.key === appKey);
              if (app) app.permissionId = null;
              const updated = { ...this.permissionsByApp };
              delete updated[appKey];
              this.permissionsByApp = updated;
            }
            await this.syncUserRolesApp();
            await this.loadPermissions();
            this.userApps = this.createAppsOptions();
            this.$toast?.success?.(`Permisos eliminados correctamente`);
          }
          return;
        }

        if (this.isEditUser) {
          const appKey = payload?.appKey || null;
          const moduleKey = payload?.module || payload?.moduleKey || null;
          this.schedulePermissionSave(appKey, moduleKey);
        }
      },
      schedulePermissionSave(appKey, moduleKey) {
        const key = appKey || "__all__";
        clearTimeout(this.permissionsSaveTimers[key]);
        this.permissionsSaveTimers[key] = setTimeout(() => this.flushPermissionApp(key, moduleKey), 350);
      },
      async flushPermissionApp(appKey, moduleKey) {
        if (!this.isEditUser) return;
        if (this.permissionsSaving[appKey]) {
          this.pendingAfterSave.add(appKey);
          return;
        }

        this.permissionsSaving = { ...this.permissionsSaving, [appKey]: true };

        const previousModules = this.permissionsByApp?.[appKey]?.modules || [];

        const assignedBy = this.$store.getters["user/getProfile"]?._id || "";
        const targetKeys = appKey === "__all__" ? [] : [appKey];
        const { success, error, toSave } = await this.savePermissionsForUser(this.editingUserId, assignedBy, targetKeys);
        if (!success) {
          this.$toast?.error?.(error);
        } else {
          this.permissionsDirty = false;
          const savedEntry = toSave?.find((item) => item?.payload?.app === appKey) || toSave?.[0] || null;
          const savedModules = savedEntry?.payload?.modules || [];

          const findChangedModule = (savedList = [], prevList = []) => {
            for (const sm of savedList) {
              const pm = prevList.find((p) => p.module === sm.module) || { actions: [] };
              const a = Array.isArray(sm.actions) ? sm.actions : [];
              const b = Array.isArray(pm.actions) ? pm.actions : [];
              if (a.length !== b.length) return sm;
              if (a.some((x) => !b.includes(x)) || b.some((x) => !a.includes(x))) return sm;
            }
            return null;
          };

          let changed = null;
          if (moduleKey) {
            const saved = savedModules.find(({ module }) => module === moduleKey) || null;
            const prev = previousModules.find(({ module }) => module === moduleKey) || { actions: [] };

            const a = Array.isArray(saved?.actions) ? saved.actions : [];
            const b = Array.isArray(prev?.actions) ? prev.actions : [];

            const modulesDiffer = a.length !== b.length || a.some((x) => !b.includes(x)) || b.some((x) => !a.includes(x));
            if (modulesDiffer) changed = saved?.actions.length ? saved : { ...prev, actions: [] };
          } else {
            changed = findChangedModule(savedModules, previousModules);
          }
          if (changed) {
            const message = Array.isArray(changed?.actions) && changed.actions.length ? "activado" : "desactivado";
            const displayName = this.dictStatistics[changed?.module] || this.dictStatistics[appKey] || changed?.module || appKey;
            this.$toast?.success?.(`Módulo ${displayName} ${message} correctamente`);
          }
        }

        this.permissionsSaving = { ...this.permissionsSaving, [appKey]: false };

        if (this.pendingAfterSave.has(appKey)) {
          this.pendingAfterSave.delete(appKey);
          await this.flushPermissionApp(appKey);
        }
      },
      createAppsOptions() {
        const rolesApp = this.$store.state.user.dataUser.rolesApp;
        const apps = this.getAppsInformation;

        const priorityOrder = ["MySpace1A", "Reservas", "Registro", "administración", "Chat"];

        apps.sort((a, b) => {
          const priorityA = priorityOrder.indexOf(a.name);
          const priorityB = priorityOrder.indexOf(b.name);

          if (priorityA === -1 || priorityB === -1) {
            return 0;
          }
          return priorityA - priorityB;
        });

        const appsWithRole = Object.values(apps).map(({ name, icon, key }) => {
          const entry = rolesApp?.[key];
          const permission = this.permissionsByApp?.[key];
          const role = permission?.role || this.getRoleFromEntry(entry);
          const modules = permission?.modules || this.getModulesFromEntry(entry);
          const permissionId = permission?._id || this.getPermissionIdFromEntry(entry);
          const isSelected = !!role;

          return { name, key, icon, role, isSelected, modules, permissionId };
        });

        const allApp = { name: "Todas", key: "all", role: "", icon: "/img/icons/apple-touch-icon-76x76.png", isSelected: false };

        allApp.role = appsWithRole.every((app) => app.role && app.role === appsWithRole[0].role) ? appsWithRole[0].role : "";
        allApp.isSelected = !!allApp.role;

        appsWithRole.push(allApp);
        return appsWithRole;
      },
      selectApp(app, isSelected, role) {
        const newRole = isSelected ? role : role || this.userApps.find((item) => item.key === app)?.role || "";

        if (app === "all") {
          this.userApps.forEach((userApp) => {
            this.setDataApp({ app: userApp, isSelected, newRole });
          });
          return;
        }

        const appToChange = this.userApps.find((userApp) => app === userApp.key);
        this.setDataApp({ app: appToChange, isSelected, newRole });

        const allApp = this.userApps.find((userApp) => "all" === userApp.key);
        const appsWithRole = this.userApps.filter((userApp) => "all" !== userApp.key);

        const allRole = appsWithRole.every((app) => app.role && app.role === appsWithRole[0].role) ? appsWithRole[0].role : "";
        const allIsSelected = !!allRole;

        this.setDataApp({ app: allApp, isSelected: allIsSelected, newRole: allRole });
      },
      setDataApp({ app, isSelected = false, newRole = "" }) {
        app.isSelected = isSelected;
        app.role = newRole;
      },
      updateStyles(reference, response) {
        reference.style.display = response ? "none" : "block";
      },
      verifyMySpace(ref, data) {
        const reference = this.$refs[ref];
        const response = !!data;

        reference.innerText = !response ? `Seleccione un rol en MySpace` : "";

        this.updateStyles(reference, response);
        return response;
      },
      verifyNext() {
        const roleMySpace = this.getAppsSelectedWithRole.find(({ key }) => key === "autologin")?.role || "";
        const verifyMySpace = this.verifyMySpace("apps", roleMySpace);

        return verifyMySpace;
      },
      async syncUserRolesApp() {
        if (!this.isEditUser || !this.editingUserId) return;

        const rolesAppEntries = this.getAppsSelectedWithRole.map(({ key, role, modules, permissionId }) => ({
          [key]: this.buildRoleEntry(role, modules, permissionId),
        }));
        const rolesApp = Object.assign({}, ...rolesAppEntries);
        const autologinRole = this.getRoleFromEntry(rolesApp.autologin);
        const roles = autologinRole ? [autologinRole] : [];

        this.$store.state.user.dataUser.rolesApp = rolesApp;
        this.$store.state.user.dataUser.roles = roles;

        try {
          const { success, error } = await this.$store.dispatch("user/updateUser", {
            _id: this.editingUserId,
            rolesApp,
            roles,
          });

          if (error || success === false) {
            throw new Error(error || "No se pudo actualizar roles del usuario");
          }
        } catch (error) {
          this.$toast?.error?.("No se pudo sincronizar roles del usuario");
          console.log("Error syncing user roles after permissions change", error);
        }
      },
      async getHistory() {
        try {
          const { data } = await this.$axios.get(`/permissions/history?userId=${this.editingUserId}`);
          this.historyItems = Array.isArray(data?.history) ? data.history : [];
        } catch (error) {
          console.log("Error fetching permissions history", error);
          this.historyItems = [];
        }
      },
      async switchViewFunc() {
        await this.getHistory();
        this.switchView = !this.switchView;
      },
    },
    async mounted() {
      if (this.isEditUser) {
        this.verifyNext();
      }
    },
    async beforeMount() {
      await this.loadPermissions();
      this.userApps = this.createAppsOptions();
    },
  };
</script>

<template>
  <section class="applicationsUser" :key="reset">
    <div class="applicationsUser__header">
      <h2 class="applicationsUser__title">Aplicaciones *</h2>
      <span ref="apps" class="applicationsUser__error"></span>

      <button class="applicationsUser__historyIcon" @click="switchViewFunc" :style="{ background: !switchView ? '#bd0909' : '' }">
        <iconic name="historyIcon" :style="{ color: !switchView ? '#fff' : '#000' }" />
      </button>
    </div>
    <div v-if="!switchView" key="history" class="applicationsUser__history">
      <p class="applicationsUser__sub">Aquí verás el detalle de los permisos solo para el modulo de <b>estadisticas</b> y quién los otorgó una vez que se asignen.</p>

      <NoFoundData v-if="historyCards.length === 0" class="applicationsUser__nofound" title="Sin actividad en el historial." icon="info" />

      <historyCardVue v-for="item in historyCards" :key="item._id" v-bind="item" />
    </div>

    <div v-else key="apps" class="applicationsUser__containerApps">
      <ApplicationCardRole v-for="app in userApps" :key="app.id || app.name" :app="app" :selectApp="selectApp" @permissions-change="handlePermissionsChange" />
    </div>
  </section>
</template>

<style lang="scss">
  .applicationsUser {
    @include Flex(column, flex-start, flex-start);
    width: 100%;
    gap: $mpadding;

    &__header {
      @include Flex(row, space-between, center);
      width: 100%;
    }

    &__historyIcon {
      @include Flex(row, center, center);
      margin-right: 10px;
      cursor: pointer;
      font-size: 22px;
      border: none;
      outline: none;
      background: unset;
      padding: 8px;
      transition: all 0.3s ease;
      border-radius: 8px;
      &:hover {
        background: #d8d8d8;
      }
    }

    &__title {
      font-weight: lighter;
      font-size: 24px;
    }

    &__sub {
      font-family: $newFont_regular;
      font-size: 15px;
      color: #595959;
      padding: 0px 10px;
    }

    &__nofound {
      position: relative;
    }

    &__containerApps {
      @include Flex(column, flex-start, flex-start);
      width: 100%;
      gap: 13px;
    }
    &__history {
      @include Flex(column, flex-start, flex-start);
      width: 100%;
      height: 520px;
      gap: 13px;
    }
    &__error {
      display: none;
      font-weight: 600;
      font-size: 0.8em;
      color: $primary-color;
    }
  }
</style>
